import React, { useState, useCallback, useEffect, useRef } from 'react'
import axios from 'axios'

// Voicemail play button → inline <audio> player.
// Audio is fetched as a blob (axios sends auth cookies; a bare <audio src>
// pointed cross-origin at the API would not) and played via an object URL.
// `src` is the full audio endpoint URL. Remount (key) to reset between items.

const IconPlay = ({ size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
)

export default function VoicemailPlayer({ src, label = 'Play voicemail' }) {
  const [audioSrc, setAudioSrc] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const objectUrlRef = useRef(null)

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
  }, [])

  const loadAudio = useCallback(async () => {
    if (loading) return
    setLoading(true)
    setError(false)
    try {
      const res = await axios.get(src, { responseType: 'blob' })
      // A proxy error page (nginx 502, SPA index.html) can arrive as a 200
      // blob — only hand real audio to the <audio> element
      if (!String(res.data?.type || '').startsWith('audio')) {
        setError(true)
        return
      }
      const objectUrl = URL.createObjectURL(res.data)
      objectUrlRef.current = objectUrl
      setAudioSrc(objectUrl)
    } catch (e) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [src, loading])

  if (audioSrc) {
    return (
      <audio
        controls
        autoPlay
        src={audioSrc}
        style={{ width: '100%', height: 32, marginBottom: 10 }}
      />
    )
  }

  return (
    <div
      onClick={loadAudio}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') loadAudio() }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 12px',
        marginBottom: 10,
        borderRadius: 6,
        border: '1px solid var(--border)',
        background: 'var(--surface-2)',
        color: error ? 'var(--risk)' : 'var(--ink-2)',
        fontSize: 12,
        fontWeight: 600,
        cursor: loading ? 'default' : 'pointer',
        userSelect: 'none',
        opacity: loading ? 0.6 : 1,
      }}
    >
      <IconPlay size={12} />
      <span>
        {loading ? 'Loading…' : error ? 'Could not load audio — retry' : label}
      </span>
    </div>
  )
}
